import {
  AdgroupConfig,
  AdgroupFormData,
  CampaignFormData,
  GenderType,
  IOS14Type,
  TargetingExpansionType
} from '@tt4b/ttam-enums';
import { computed, Ref, ref } from '@vue/composition-api';
import { targetingExpansionItem, targetingExpansionRelatedFields } from './types';
import { i18n } from './../../../i18n';
import { useStdAdgroupIs, ValidationError } from '@tt4b/ttam-hooks';

export function useRules() {
  return {
    targetExpansionValidator: computed(() => [
      {
        validator: (rule, value, callback, data: AdgroupFormData) => {
          if (data.targetingExpansion?.expansion_enabled && !data.targetingExpansion?.expansion_types?.length) {
            return callback(
              new ValidationError({
                message: i18n.t('ad_group_targeting_expansion_error'),
                messageI18nKey: 'ad_group_targeting_expansion_error',
              })
            );
          }
          return callback();
        },
        trigger: 'custom',
      },
    ]),
  };
}

export function usePropsSwitchDisabled(opt: {
  adgroupData: Ref<AdgroupFormData>;
  campaignData: Ref<CampaignFormData>;
  targetingExpansionOptions: Ref<targetingExpansionItem[]>;
}) {
  const { adgroupData, campaignData, targetingExpansionOptions } = opt;
  const { isConversionApp } = useStdAdgroupIs({ adgroupFormData: adgroupData, campaignFormData: campaignData });
  const isDedicatedCampaign = computed(() => adgroupData.value.targetDeviceVersion === IOS14Type.ABOVE);
  const allExpansionOptionsDisabled = computed(() => targetingExpansionOptions.value.every(({ disabled }) => disabled));
  const switchDisabled = computed(
    () =>
      (isDedicatedCampaign.value && !adgroupData.value.targetingExpansion?.expansion_enabled) ||
      allExpansionOptionsDisabled.value ||
      isConversionApp.value
  );
  const switchDisabledHint = computed(() =>
    (isDedicatedCampaign.value ?
      i18n.t('module_adgroup_target_expansion_dc_tip') :
      i18n.t('ad_group_targeting_expansion_disabled_tip'))
  );
  return {
    switchDisabled,
    switchDisabledHint,
  };
}

export function useTargetingExpansionOPtions(opt: { adgroupData: Ref<AdgroupFormData> }) {
  const { adgroupData } = opt;

  const targetingExpansionOptions = computed<targetingExpansionItem[]>(() => {
    // const { isInSplitTestLevel } = useSplitTestTool();
    // const { testAdroupFormData } = useSplitTestStore();

    const isInSplitTestLevel = ref(false);
    const testAdroupFormData = ref<AdgroupFormData>();
    const adgroupFormData = isInSplitTestLevel.value ? testAdroupFormData : adgroupData;

    const adgroup = adgroupFormData.value as Pick<
      typeof adgroupData.value,
      typeof targetingExpansionRelatedFields[number]
    >;

    return [
      {
        type: TargetingExpansionType.AUDIENCE,
        label: i18n.t('adgroup_audience'),
        disabled: !adgroup.retargetingTags?.length && !adgroup.retargetingTagsExclude?.length,
      },
      {
        type: TargetingExpansionType.GENDER,
        label: i18n.t('adgroup_gender'),
        disabled: ![GenderType.MALE, GenderType.FEMALE].includes(adgroup.gender),
      },
      {
        type: TargetingExpansionType.AGE,
        label: i18n.t('adgroup_age'),
        disabled: adgroup.age?.[0] === 'ALL',
      },
      {
        type: TargetingExpansionType.INTEREST_BEHAVIOR,
        label: i18n.t('module_adgroup_interest_behavior'),
        disabled:
          !adgroup.adTagV2?.length &&
          !adgroup.inMarketTags?.length &&
          !adgroup.actionCategoriesV2?.some?.(i => i.length) &&
          !adgroup.interestKeywordsI18n?.length,
      },
    ];
  });
  return {
    targetingExpansionOptions,
  };
}

export function useHighlightTargetingExpansion() {
  const isHighlighting = ref(false);
  let timerId;
  function highlight() {
    if (timerId !== undefined) {
      clearTimeout(timerId);
      timerId = undefined;
    }

    isHighlighting.value = true;
    timerId = setTimeout(() => {
      isHighlighting.value = false;
      timerId = undefined;
    }, 2000);
  }

  return { isHighlighting, highlight };
}

export function useAppRetargetingNoSupportAlertVisible(opt: {
  adgroupData: Ref<AdgroupFormData>;
  campaignData: Ref<CampaignFormData>;
  adgroupConfig: Ref<AdgroupConfig>;
}) {
  const { adgroupConfig, adgroupData, campaignData } = opt;
  const { isConversionApp } = useStdAdgroupIs({ adgroupFormData: adgroupData, campaignFormData: campaignData });
  const appRetargetingNoSupportAlertVisible = computed(
    () => adgroupConfig.value?.showTargetExpendDisabledWithConversionApp && isConversionApp.value
  );
  return {
    appRetargetingNoSupportAlertVisible,
  };
}
